import {useCallback, useMemo, useState} from 'react';

import dot from 'dot-object';
import {SortingDirections} from 'src/components/filter-header';

export type SortConfig<P> = {
    key?: P,
    direction: SortingDirections,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const comparator = <P extends string>(direction: SortingDirections) => (a: any, b: any, key: P) => {
    const firstValueToCompare = (dot.pick(key, a) || '').trim().toLowerCase();
    const secondValueToCompare = (dot.pick(key, b) || '').trim().toLowerCase();

    if (firstValueToCompare.length === 0) {
        return 1;
    }

    if (secondValueToCompare.length === 0) {
        return -1;
    }

    return direction === SortingDirections.DESC
        ? firstValueToCompare.localeCompare(secondValueToCompare)
        : secondValueToCompare.localeCompare(firstValueToCompare);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useSortingRows = <T, P extends string>(rows: Record<string, any>[]) => {
    const [sortConfig, setSortConfig] = useState<SortConfig<P>>({direction: SortingDirections.ASC});

    const sortedRows = useMemo(() => {
        const getSortedColumn = ({key, direction}: SortConfig<P>) => {
            if (key) {
                const sortFn = comparator(direction);
                return rows.sort((a, b) => sortFn(a, b, key));
            }

            return rows;
        };

        return getSortedColumn(sortConfig);
    }, [rows, sortConfig]) as T[];

    const onSort = useCallback((config: SortConfig<P>) => {
        setSortConfig(prevState => ({...prevState, ...config}));
    }, []);

    return {sortConfig, onSort, sortedRows};
};
